/* global BX24 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Avatar,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  NotificationsOutlined as NotificationsIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

const BellIntroNotification = () => {
  const [show, setShow] = useState(false);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);

  // Логика получения данных пользователя
  const fetchUserFromDeal = async (dealId) => {
    if (!dealId) {
      console.warn("⚠️ ID сделки не передан, пропускаем запрос для уведомления о колокольчике.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${PATH_TO_BACKEND}/api/deal-assigned-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ dealId }),
      });

      if (!response.ok) {
        throw new Error("Ошибка при получении ответственного пользователя");
      }

      const data = await response.json();

      if (data.assignedUserId) {
        setUserName(data.assignedUserName || 'Unknown User');
        const userId = data.assignedUserId;
        await checkBellIntroStatus(userId);
      } else {
        console.warn("❌ Не удалось получить ID ответственного пользователя.");
        setLoading(false);
      }
    } catch (error) {
      console.error("⚠️ Ошибка при запросе данных пользователя:", error.message);
      setLoading(false);
    }
  };

  const getCurrentUserFromDeal = useCallback(async () => {
    const placementInfo = BX24.placement?.info?.();

    if (placementInfo?.options?.ID) {
      const dealId = placementInfo.options.ID.toString();
      console.log("✅ ID сделки для уведомления о колокольчике:", dealId);
      await fetchUserFromDeal(dealId);
    } else {
      console.warn("❌ Не удалось получить ID сделки для уведомления о колокольчике.");
      setLoading(false);
    }
  }, []);

  const checkBellIntroStatus = async (userId) => {
    try {
      const response = await fetch(`${PATH_TO_BACKEND}/api/notification-status/${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        
        // Показываем только если bell intro не был просмотрен
        if (!data.user.bell_intro_viewed) {
          setShow(true);
          console.log('✅ Показываем уведомление о колокольчике для пользователя:', data.user.name);
        } else {
          console.log('ℹ️ Пользователь уже видел уведомление о колокольчике');
        }
      } else {
        console.log('⚠️ Пользователь не найден в базе данных');
      }
      setLoading(false);
    } catch (error) {
      console.error('⚠️ Ошибка при проверке статуса уведомления о колокольчике:', error);
      setLoading(false);
    }
  };

  // Инициализация
  useEffect(() => {
    if (typeof BX24 === 'undefined' || BX24 === null) {
      console.log('BX24 API недоступен - уведомление о колокольчике не показываем (разработка)');
      setLoading(false);
      return;
    }

    try {
      BX24.init(() => {
        console.log('BX24 инициализирован для проверки уведомления о колокольчике');
        getCurrentUserFromDeal();
      });
    } catch (error) {
      console.log('Ошибка при работе с BX24 API:', error.message);
      setLoading(false);
    }
  }, [getCurrentUserFromDeal]);

  const handleClose = async () => {
    setShow(false);
    
    // Отмечаем как просмотренное в БД
    try {
      if (typeof BX24 !== 'undefined' && BX24 !== null) {
        const placementInfo = BX24.placement?.info?.();
        if (placementInfo?.options?.ID) {
          const dealId = placementInfo.options.ID.toString();
          const response = await fetch(`${PATH_TO_BACKEND}/api/deal-assigned-user`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ dealId }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.assignedUserId) {
              await fetch(`${PATH_TO_BACKEND}/api/bell-intro-viewed/${data.assignedUserId}`, {
                method: 'POST',
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Ошибка при сохранении статуса:', error);
    }

    // Размер окна автоматически восстановится через bitrixResize.handleModalClose() в useEffect
  };

  // Обработчик для подсветки кнопки колокольчика
  const handleShowBell = () => {
    // Подсвечиваем кнопку колокольчика
    const notificationButton = document.querySelector('[title*="Что нового"]');
    if (notificationButton) {
      notificationButton.style.animation = 'pulse 3s infinite';
      notificationButton.style.boxShadow = '0 0 0 4px rgba(244, 67, 54, 0.4)';
      
      // Убираем подсветку через 8 секунд
      setTimeout(() => {
        notificationButton.style.animation = '';
        notificationButton.style.boxShadow = '';
      }, 8000);
    }
    handleClose();
  };


  if (loading || !show) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999
      }}
      onClick={handleClose}
    >
      <div
        style={{
          position: 'absolute',
          top: '120px', // Позиционируем ниже Header
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '500px',
          width: '90vw',
          maxHeight: '70vh',
          overflow: 'auto'
        }}
        onClick={e => e.stopPropagation()}
      >
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 56, height: 56 }}>
            <NotificationsIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" color="text.primary" fontWeight="600">
              {userName && `Привет, ${userName}!`}
            </Typography>
            <Typography variant="h6" color="text.primary" fontWeight="600">
              🔔 Новая возможность!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              В калькуляторе появились уведомления
            </Typography>
          </Box>
        </Box>

        <Card elevation={0} sx={{ backgroundColor: '#e3f2fd', borderRadius: 2, p: 2, mb: 3 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <NotificationsIcon sx={{ color: '#1976d2' }} />
              <Typography variant="subtitle1" fontWeight="600" color="#1976d2">
                Кнопка уведомлений
              </Typography>
              <Chip label="Новое" size="small" color="primary" variant="outlined" />
            </Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
              В правом верхнем углу появилась кнопка уведомлений. Теперь вы можете в любое время посмотреть информацию о новых функциях калькулятора.
            </Typography>
            <Box display="flex" alignItems="center" gap={1} justifyContent="center" sx={{ 
              backgroundColor: 'rgba(25, 118, 210, 0.1)', 
              borderRadius: 1, 
              p: 1 
            }}>
              <NotificationsIcon fontSize="small" color="primary" />
              <Typography variant="body2" color="primary" fontWeight="500">
                Красный значок показывает количество новых уведомлений
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Box display="flex" gap={2}>
          <Button 
            onClick={handleShowBell}
            variant="contained"
            fullWidth
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              color: 'white',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)'
              }
            }}
          >
            Показать кнопку уведомлений! 👆
          </Button>
          <Button 
            onClick={handleClose}
            variant="outlined"
            sx={{
              color: '#757575',
              borderColor: '#e0e0e0',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#bdbdbd',
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            Понятно
          </Button>
        </Box>
      </div>
    </div>
  );
};

export default BellIntroNotification;